import React, { useState, useMemo } from 'react';
import { Search, Plus, X } from 'lucide-react';
import { POPULAR_TOOLS, TOOL_CATEGORIES, type ToolSelection, type ToolCategory } from '../constants/taskCreation';

interface Props {
  selected: ToolSelection[];
  onChange: (tools: ToolSelection[]) => void;
}

const ToolPicker: React.FC<Props> = ({ selected, onChange }) => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<ToolCategory | 'all'>('all');
  const [customName, setCustomName] = useState('');

  const selectedIds = useMemo(() => new Set(selected.map(t => t.id)), [selected]);

  const filtered = useMemo(() => {
    let tools = POPULAR_TOOLS;
    if (activeCategory !== 'all') {
      tools = tools.filter(t => t.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      tools = tools.filter(t => t.name.toLowerCase().includes(q));
    }
    return tools;
  }, [search, activeCategory]);

  const toggle = (tool: typeof POPULAR_TOOLS[0]) => {
    if (selectedIds.has(tool.id)) {
      onChange(selected.filter(t => t.id !== tool.id));
    } else {
      onChange([...selected, { id: tool.id, name: tool.name, category: tool.category }]);
    }
  };

  const addCustom = () => {
    const name = customName.trim();
    if (!name) return;
    const id = `custom-${name.toLowerCase().replace(/\s+/g, '-')}`;
    if (selectedIds.has(id)) return;
    onChange([...selected, { id, name, category: 'other', isCustom: true }]);
    setCustomName('');
  };

  return (
    <div className="space-y-4">
      {/* Selected tools */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 pb-2">
          {selected.map(tool => {
            const dbTool = POPULAR_TOOLS.find(t => t.id === tool.id);
            const color = dbTool?.color || '#64748B';
            return (
              <span
                key={tool.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all animate-fade-in"
                style={{
                  background: `${color}20`,
                  border: `1px solid ${color}50`,
                  color: color === '#FFFFFF' || color === '#000000' ? '#e8eaed' : color,
                }}
              >
                {tool.name}
                <button
                  type="button"
                  onClick={() => onChange(selected.filter(t => t.id !== tool.id))}
                  className="hover:opacity-70 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tech-text-muted" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search tools..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-tech-gray border border-tech-border text-sm text-tech-text placeholder-tech-text-muted/50 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 focus:border-tech-cyan transition-all"
        />
      </div>

      {/* Category tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
        <button
          type="button"
          onClick={() => setActiveCategory('all')}
          className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            activeCategory === 'all'
              ? 'bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30'
              : 'text-tech-text-muted hover:text-tech-text border border-transparent hover:border-tech-border'
          }`}
        >
          All
        </button>
        {TOOL_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            type="button"
            onClick={() => setActiveCategory(cat.id)}
            className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeCategory === cat.id
                ? 'bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30'
                : 'text-tech-text-muted hover:text-tech-text border border-transparent hover:border-tech-border'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Tool grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[280px] overflow-y-auto pr-1 scrollbar-thin">
        {filtered.map(tool => {
          const isSelected = selectedIds.has(tool.id);
          return (
            <button
              key={tool.id}
              type="button"
              onClick={() => toggle(tool)}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all border"
              style={{
                borderColor: isSelected ? `${tool.color}60` : 'rgba(255,255,255,0.06)',
                background: isSelected ? `${tool.color}15` : 'rgba(26,26,26,0.4)',
                color: isSelected
                  ? (tool.color === '#FFFFFF' || tool.color === '#000000' ? '#e8eaed' : tool.color)
                  : '#9ca3af',
              }}
            >
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ background: tool.color === '#FFFFFF' ? '#888' : tool.color }}
              />
              <span className="truncate">{tool.name}</span>
              {isSelected && <span className="ml-auto text-[10px]">✓</span>}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="col-span-full text-center text-tech-text-muted text-xs py-6">
            No tools found — add a custom one below
          </p>
        )}
      </div>

      {/* Custom tool */}
      <div className="flex gap-2">
        <input
          type="text"
          value={customName}
          onChange={e => setCustomName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustom())}
          placeholder="Add a tool not listed..."
          className="flex-1 px-3 py-2 rounded-xl bg-tech-gray border border-tech-border text-sm text-tech-text placeholder-tech-text-muted/50 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 focus:border-tech-cyan transition-all"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!customName.trim()}
          className="px-3 py-2 rounded-xl bg-tech-cyan/10 border border-tech-cyan/30 text-tech-cyan text-sm font-medium hover:bg-tech-cyan/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ToolPicker;
